import { useEffect, useState } from "react";
import { getFileNameBasedOnCurrentDateTime } from "../utils/file";
import getNewOrdersModuleAssetUrl from "mocks/worker/getNewOrdersModuleAssetUrl.ts";
import { PostOrdersRequestBody } from "types/api/orders/postOrders";
import { useSelector } from "react-redux";
import { RootState } from "stores/application.store.tsx";
import { AddOnText, CustomColorText } from "constants/text";
import { NewOrderNewOrderLabel } from "components/NewOrders/constants";
import { ConfigurationGroup } from "components/NewOrders/utils/NewOrderUtils";
import { models } from "types/api/viewModels";
import { ValidateProductsResponseData } from "types/api/products/postValidate";
import { OrderType, UserRoleType } from "types/declarations/enums.ts";

const useNewOrdersPDFGenerator = (
	configurationGroups: ConfigurationGroup[] | null | undefined,
	headerText: string,
	subtotal: number,
	orderDetails?: PostOrdersRequestBody | undefined,
	isReview?: boolean,
	isConfirmation?: boolean,
	originalOrderDetails?: models["OrderDetailViewModel"] | null,
	isAddOn?: boolean,
	isCustomColor?: boolean,
	csvData?: (models["ValidatedOrderViewModel"] | undefined)[],
	validatedOrderResponse?: ValidateProductsResponseData,
	userRole?: UserRoleType,
	merchCartItems?: models["ShoppingCartItem"][] | null,
	merchGroup?: ConfigurationGroup | null
) => {
	const [pdfData, setPdfData] = useState<PDFData>({ blob: null, loading: false, error: null });
	const merchAccountData = useSelector((state: RootState) => state.marketingMaterialsCart.selectedMerchAccount);
	const productLineAccountInfo = useSelector((state: RootState) => state.newOrder.productLineAccounts);

	const generateNewOrdersPDF = () => {
		const worker = getNewOrdersModuleAssetUrl();

		const plInfo =
			orderDetails?.orderType === OrderType.MERCH ? { ["MERCH"]: [merchAccountData] } : productLineAccountInfo;

		worker.postMessage({
			configurationGroups,
			headerText,
			subtotal,
			orderDetails,
			isReview,
			isConfirmation,
			productLineAccountInfo: plInfo,
			originalOrderDetails,
			isAddOn,
			isCustomColor,
			csvData,
			validatedOrderResponse,
			userRole,
			merchCartItems,
			merchGroup
		});

		worker.onmessage = (e) => {
			if (e.data.blob) {
				setPdfData({ blob: e.data.blob, loading: false, error: null });
			} else if (e.data.error) {
				setPdfData((prevData) => ({ ...prevData, loading: false, error: e.data.error }));
			}
		};

		worker.onerror = (error) => {
			setPdfData((prevData) => ({
				...prevData,
				loading: false,
				error: error instanceof Error ? error : new Error("An unknown error occurred")
			}));
		};

		setPdfData((prevData) => ({ ...prevData, loading: true }));
	};
	const jobName = orderDetails?.jobName ? `_${orderDetails.jobName}` : "";
	const poNumber = orderDetails?.poNumber ? `_PO${orderDetails.poNumber}` : "";
	let orderType = NewOrderNewOrderLabel;
	if (isAddOn) {
		orderType = AddOnText;
	} else if (isCustomColor) {
		orderType = CustomColorText;
	}
	const filePrefix = `${orderType.replace(" ", "")}${jobName}${poNumber}`;

	useEffect(() => {
		if (pdfData.blob) {
			const url = window.URL.createObjectURL(pdfData.blob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `${getFileNameBasedOnCurrentDateTime(filePrefix)}.pdf`);
			document.body.appendChild(link);
			link.click();
			setPdfData({ blob: null, loading: false, error: null });
		}
	}, [pdfData.blob, jobName, poNumber, filePrefix]);

	return { pdfData, generateNewOrdersPDF };
};

interface PDFData {
	blob: Blob | null;
	loading: boolean;
	error: Error | null;
}

export default useNewOrdersPDFGenerator;
